import React, { useState, useEffect } from 'react';
import { agregarTurno, obtenerVigilantesPorServicio, obtenerDetallesServicio } from '../data/FirestoreCuadrantes';
import { Button, Alert, Box, Typography, CircularProgress } from '@mui/material';
import './CreateTurno.scss';
import { extraerDatosLocalizacion, buscarServicioFreematica, obtenerTagPersona, crearMarcajeTurno } from '../services/ApiMarcajes';

// Función helper para formatear el nombre del vigilante
const formatearVigilante = (vigilante) => {
  const nombreCompleto = `${vigilante.nombre || ''} ${vigilante.apellidos || ''}`.trim();
  const persona = vigilante.persona || '';
  
  if (persona && nombreCompleto) {
    return `${persona} - ${nombreCompleto}`;
  } else if (persona) {
    return persona;
  } else if (nombreCompleto) {
    return nombreCompleto;
  } else {
    return vigilante.id || 'Sin nombre';
  }
};

const CreateTurno = ({ cuadranteId, currentService, servicioId, onClose }) => {
  // Estados originales
  const [inicioTurno, setInicioTurno] = useState('');
  const [finTurno, setFinTurno] = useState('');
  const [arcos, setArcos] = useState(false);
  const [manejoExplosivos, setManejoExplosivos] = useState(false);
  const [peligrosidad, setPeligrosidad] = useState(false);
  const [conArma, setConArma] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [fechaSeleccionada, setFechaSeleccionada] = useState('');
  const [vigilantes, setVigilantes] = useState([]);
  const [vigilanteSeleccionado, setVigilanteSeleccionado] = useState({ id: '', persona: '', tag: '' });
  const [enviandoMarcaje, setEnviandoMarcaje] = useState(false);
  const [errorMarcaje, setErrorMarcaje] = useState('');
  const [exitoMarcaje, setExitoMarcaje] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Campos para Freematica
  const [empresa, setEmpresa] = useState('0001');
  const [detallesServicio, setDetallesServicio] = useState(null);

  useEffect(() => {
    // Obtener detalles del servicio seleccionado
    const cargarDetallesServicio = async () => {
      try {
        setLoading(true);
        const servicioIdToUse = Array.isArray(servicioId) ? servicioId[0] : servicioId;
        if (servicioIdToUse) {
          const detalles = await obtenerDetallesServicio(servicioIdToUse, currentService);
          setDetallesServicio(detalles);
          
          // Establecer valores por defecto de empresa si está disponible
          if (detalles && detalles.empresa) {
            setEmpresa(detalles.empresa);
          }
        }
      } catch (error) {
        console.warn('Error al cargar detalles del servicio:', error.message);
        setErrorMarcaje('Error al cargar detalles del servicio: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    cargarDetallesServicio();
  }, [servicioId, currentService]);

  useEffect(() => {
    const fetchVigilantes = async () => {
      try {
        setErrorMarcaje('');
        setVigilantes([]);
        
        // Determinar qué servicioId usar
        const servicioIdToUse = Array.isArray(servicioId) && servicioId.length > 0 
          ? servicioId[0] 
          : servicioId;

        if (!servicioIdToUse) {
          console.warn("No se proporcionó servicioId válido");
          setErrorMarcaje('No se pudo determinar el servicio para cargar usuarios');
          return;
        }

        console.log('Obteniendo usuarios para servicio:', servicioIdToUse);
        
        const vigilantesData = await obtenerVigilantesPorServicio(servicioIdToUse, currentService);
        
        if (vigilantesData && vigilantesData.length > 0) {
          // Asegurarnos que cada usuario tenga un código de persona (no solo un ID)
          const vigilantesConDatos = vigilantesData.map(vigilante => {
            // Si no tiene persona pero tiene nombre/apellidos, crear uno
            if (!vigilante.persona && (vigilante.nombre || vigilante.apellidos)) {
              return {
                ...vigilante,
                persona: `${vigilante.nombre || ''} ${vigilante.apellidos || ''}`.trim()
              };
            }
            return vigilante;
          });
          
          setVigilantes(vigilantesConDatos);
        } else {
          console.warn("No se encontraron usuarios para este servicio");
          setErrorMarcaje('No se encontraron usuarios asignados a este servicio');
        }
      } catch (error) {
        console.warn('Error al obtener usuarios:', error.message);
        setErrorMarcaje('Error al cargar usuarios: ' + error.message);
      }
    };

    fetchVigilantes();
  }, [currentService, servicioId]);

  const handleInicioTurnoChange = (event) => {
    setInicioTurno(event.target.value);
  };

  const handleFinTurnoChange = (event) => {
    setFinTurno(event.target.value);
  };

  const handleArcosChange = () => {
    setArcos(!arcos);
  };

  const handleManejoExplosivosChange = () => {
    setManejoExplosivos(!manejoExplosivos);
  };

  const handlePeligrosidadChange = () => {
    setPeligrosidad(!peligrosidad);
  };

  const handleConArmaChange = () => {
    setConArma(!conArma);
  };

  const handleObservacionesChange = (event) => {
    setObservaciones(event.target.value);
  };

  const handleFechaSeleccionadaChange = (event) => {
    setFechaSeleccionada(event.target.value);
  };

  const handleVigilanteChange = (event) => {
    const vigilanteId = event.target.value;
    const vigilante = vigilantes.find(v => v.id === vigilanteId) || { id: '', persona: '', tag: '' };
    
    // Verificación adicional si el usuario tiene código de persona válido
    if (!vigilante.persona || vigilante.persona === 'undefined' || vigilante.persona === 'null') {
      console.warn("Usuario sin código de persona válido");
      setErrorMarcaje("El usuario seleccionado no tiene un código de persona válido, se requiere para Freematica");
      return;
    }
    
    setVigilanteSeleccionado(vigilante);
  };

  const handleEmpresaChange = (event) => {
    setEmpresa(event.target.value);
  };

  // Función para testear la conexión con Freematica
  const testFreematica = async () => {
    if (!detallesServicio || !detallesServicio.localizacion) {
      setErrorMarcaje('No hay datos de localización disponibles para probar');
      return;
    }
    
    if (!vigilanteSeleccionado || !vigilanteSeleccionado.id || !vigilanteSeleccionado.persona) {
      setErrorMarcaje('Seleccione un usuario con código de persona válido para probar la conexión');
      return;
    }
    
    setEnviandoMarcaje(true);
    setErrorMarcaje('');
    
    try {
      // 1. Extraer datos de localización
      const { contrato, delegacion, servicio } = extraerDatosLocalizacion(detallesServicio.localizacion);
      
      // 2. Buscar servicio
      const servicioInfo = await buscarServicioFreematica(
        empresa,
        delegacion,
        contrato,
        servicio
      );
      
      // 3. Obtener tag de persona para la empresa del servicio
      const personTag = await obtenerTagPersona(vigilanteSeleccionado.persona, empresa);
      
      setErrorMarcaje('');
      setExitoMarcaje('Prueba de conexión exitosa. La integración con Freematica funciona correctamente.');
    } catch (error) {
      console.warn('Error en prueba de conexión:', error.message);
      setErrorMarcaje(`Error al probar conexión: ${error.message}`);
    } finally {
      setEnviandoMarcaje(false);
    }
  };

  const handleSubmitTurno = async (event) => {
    event.preventDefault();
    setEnviandoMarcaje(true);
    setErrorMarcaje('');
    setExitoMarcaje(false);

    try {
      // Validaciones básicas
      if (!vigilanteSeleccionado || !vigilanteSeleccionado.id) {
        throw new Error('Debe seleccionar un usuario');
      }

      if (!vigilanteSeleccionado.persona) {
        throw new Error('El usuario seleccionado no tiene un código de persona válido');
      }

      if (!fechaSeleccionada) {
        throw new Error('Debe seleccionar una fecha');
      }

      if (!inicioTurno || !finTurno) {
        throw new Error('Debe especificar las horas de inicio y fin');
      }

      // 1. Guardar el turno en Firestore
      const nuevoTurno = {
        inicioTurno,
        finTurno,
        arcos,
        manejoExplosivos,
        peligrosidad,
        conArma,
        observaciones,
        fecha: fechaSeleccionada,
        vigilante: vigilanteSeleccionado.persona, // Usar persona, no ID
        vigilanteId: vigilanteSeleccionado.id,
        servicioId: servicioId // Incluir el ID del servicio
      };

      await agregarTurno(cuadranteId, nuevoTurno, currentService);
      
      // 2. Registrar en Freematica si tenemos la información necesaria
      if (detallesServicio && detallesServicio.localizacion && vigilanteSeleccionado.persona) {
        try {
          // Extraer delegación y contrato del campo localización
          const { contrato, delegacion, servicio } = extraerDatosLocalizacion(detallesServicio.localizacion);

          // Buscar el servicio en Freematica
          const servicioInfo = await buscarServicioFreematica(
            empresa, 
            delegacion,
            contrato,
            servicio
          );

          // Obtener el tag de la persona para la empresa del servicio
          const personTag = await obtenerTagPersona(vigilanteSeleccionado.persona, empresa);

          // Crear los marcajes
          const resultado = await crearMarcajeTurno(
            servicioInfo,
            personTag,
            fechaSeleccionada,
            inicioTurno,
            finTurno,
            observaciones
          );
          
          setExitoMarcaje(true);
        } catch (error) {
          console.warn('Error al registrar marcaje en Freematica:', error.message);
          // Capturar respuesta de error si existe
          if (error.response) {
            console.warn('Detalles del error:', {
              status: error.response.status,
              data: error.response.data
            });
          }
          setErrorMarcaje(`Error al registrar marcaje: ${error.message}`);
        }
      } else {
        const datosFaltantes = [];
        if (!detallesServicio) datosFaltantes.push("detalles del servicio");
        if (detallesServicio && !detallesServicio.localizacion) datosFaltantes.push("localización del servicio");
        if (!vigilanteSeleccionado.persona) datosFaltantes.push("código de persona del usuario");
        
        setExitoMarcaje(true); // El turno se guardó, aunque no se pudo registrar el marcaje
        setErrorMarcaje(`Turno guardado sin registro en Freematica (faltan: ${datosFaltantes.join(", ")})`);
      }

      // Limpiar los campos del formulario después de enviar el turno
      setInicioTurno('');
      setFinTurno('');
      setArcos(false);
      setManejoExplosivos(false);
      setPeligrosidad(false);
      setConArma(false);
      setObservaciones('');
      setFechaSeleccionada('');
      setVigilanteSeleccionado({ id: '', persona: '', tag: '' });
      
      // Cerrar el modal automáticamente después de un tiempo si tuvimos éxito
      if (exitoMarcaje && onClose) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.warn('Error al procesar el turno:', error.message);
      setErrorMarcaje(error.message || 'Error al crear el turno');
    } finally {
      setEnviandoMarcaje(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-content">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography ml={2}>Cargando datos...</Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="modal-content">
      <h2>Crear Turno</h2>
      {errorMarcaje && (
        <Alert severity="error" style={{ marginBottom: '16px' }}>
          {errorMarcaje}
        </Alert>
      )}
      {exitoMarcaje && (
        <Alert severity="success" style={{ marginBottom: '16px' }}>
          {typeof exitoMarcaje === 'string' ? exitoMarcaje : 'Turno creado correctamente'}
        </Alert>
      )}
      <form onSubmit={handleSubmitTurno}>
        <label>
          Fecha:
          <input
            type="date"
            value={fechaSeleccionada}
            onChange={handleFechaSeleccionadaChange}
            required
          />
        </label>
        <label>
          Hora de inicio:
          <input
            type="time"
            value={inicioTurno}
            onChange={handleInicioTurnoChange}
            required
          />
        </label>
        <label>
          Hora de fin:
          <input
            type="time"
            value={finTurno}
            onChange={handleFinTurnoChange}
            required
          />
        </label>
        <label>
          Arcos:
          <input type="checkbox" checked={arcos} onChange={handleArcosChange} />
        </label>
        <label>
          Manejo de Explosivos:
          <input type="checkbox" checked={manejoExplosivos} onChange={handleManejoExplosivosChange} />
        </label>
        <label>
          Peligrosidad:
          <input type="checkbox" checked={peligrosidad} onChange={handlePeligrosidadChange} />
        </label>
        <label>
          Con Arma:
          <input type="checkbox" checked={conArma} onChange={handleConArmaChange} />
        </label>
        
        {/* Campos para Freematica */}
        <div className="freematica-section" style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h3>Datos para registro en Freematica</h3>
          
          <label>
            Código de Empresa:
            <select 
              value={empresa} 
              onChange={handleEmpresaChange}
              className="form-select"
            >
              <option value="0001">0001 - SEGURIDAD INTEGRAL SECOEX</option>
              <option value="0005">0005 - PRESTACIONES AUXILIARES VALENCIANAS</option>
              <option value="0006">0006 - SERV. Y FUNCIONES AUX. MANCHEGOS</option>
              <option value="0008">0008 - SCU PRACOM ANDALUCIA</option>
              <option value="0013">0013 - PRACON CENTRO DE EMPLEO MADRID</option>
              <option value="0016">0016 - TELIMAN SERVICIOS COMPLEMENTARIOS</option>
              <option value="0020">0020 - PRACON SERVIEXT</option>
            </select>
          </label>
          
          {detallesServicio && (
            <Box mt={2} p={1} bgcolor="#e8f4f8" borderRadius={1}>
              <Typography variant="subtitle2">Información del Servicio:</Typography>
              <Typography variant="body2">
                <strong>Localización:</strong> {detallesServicio.localizacion || 'No disponible'}
              </Typography>
              <Typography variant="body2">
                <strong>Contrato:</strong> {detallesServicio.contrato || 'No disponible'}
              </Typography>
              {detallesServicio.delegacion && (
                <Typography variant="body2">
                  <strong>Delegación:</strong> {detallesServicio.delegacion}
                </Typography>
              )}
              
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                onClick={testFreematica}
                disabled={enviandoMarcaje}
                style={{ marginTop: '10px', fontSize: '0.75rem' }}
              >
                Probar conexión
              </Button>
            </Box>
          )}
        </div>
        
        <label>
          Usuario:
          <select 
            value={vigilanteSeleccionado.id} 
            onChange={handleVigilanteChange}
            className="form-select"
            required
          >
            <option value="">Seleccionar usuario</option>
            {vigilantes.length > 0 ? (
              vigilantes.map((vigilante) => (
                <option key={vigilante.id} value={vigilante.id}>
                  {formatearVigilante(vigilante)}
                </option>
              ))
            ) : null}
          </select>
          {vigilantes.length === 0 && (
            <div className="no-vigilantes-message">No hay usuarios disponibles</div>
          )}
          {vigilanteSeleccionado.id && (
            <div style={{fontSize: '0.85rem', padding: '5px', backgroundColor: '#f0f0f0', marginTop: '5px', borderRadius: '4px'}}>
              <strong>Código Persona:</strong> {vigilanteSeleccionado.persona || 'No disponible'}
            </div>
          )}
        </label>
        
        <label>
          Observaciones:
          <textarea value={observaciones} onChange={handleObservacionesChange}></textarea>
        </label>
        
        <Box display="flex" justifyContent="space-between" mt={3}>
          {onClose && (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={enviandoMarcaje}
            >
              Cancelar
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={enviandoMarcaje || vigilantes.length === 0}
          >
            {enviandoMarcaje ? 'Creando...' : 'Crear Turno'}
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default CreateTurno;